/**
 * useRemoteAdvanceTimer — Schedules track advance for remote renderer playback.
 *
 * When audio plays on a network renderer (UPnP, RAAT, AirPlay, etc.), the web client
 * has no <audio> element ticking — WebAudioRenderer is paused. Track advance cannot
 * rely on the renderer's STOPPED events alone (ambiguous during buffering/URI swap).
 *
 * Two advance paths:
 *   • Position-reporting renderers (Naim, Lumin, Sonos, … on Tauri): Connector
 *     transport monitor emits renderer-track-ended on PLAYING→STOPPED. This hook
 *     does NOT schedule a primary wall-clock timer — only a late fallback if the
 *     renderer goes silent past expected end + margin.
 *   • Timer-based renderers (Ideon/MU2/AirLens-class): no reliable GetTransportInfo;
 *     wall-clock (duration + buffer) remains the primary advance mechanism.
 *
 * Timer lifecycle:
 *   playbackState → 'playing' + remote renderer  →  start timer (mode depends on renderer)
 *   playbackState → 'paused'                     →  pause timer (save remaining)
 *   playbackState → 'stopped' or 'idle'          →  cancel timer
 *   currentTrack changes                          →  cancel + restart for new duration
 *   seek (position jumps)                         →  recalculate remaining, restart timer
 */
import { useEffect, useRef } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { isTestPlaybackMode } from '../transport/QueueController';
import { isIOSNativeApp } from '../utils/platform';
import {
  registerRemoteAdvanceCancel,
  shouldDeferAdvanceToRenderer,
  isRendererGaplessAdvanceRecent,
  isRendererPositionReportingActive,
  isWallClockDueToStaleRelTime,
  shouldUseWallClockAdvance,
  getAdvanceGeneration,
  isScheduledAdvanceTokenValid,
  WALL_CLOCK_FALLBACK_MARGIN_SECS,
} from '../transport/remoteAdvanceControl';

function isNetworkRenderer(zone: any): boolean {
  if (!zone || !zone.type) return false;
  const localTypes = ['browser', 'local', 'local-native'];
  const localIds = ['this_computer', 'local_computer', 'this_device'];
  return !localTypes.includes(zone.type) && !localIds.includes(zone.id);
}

/** True when store position indicates the track is near its reported end. */
function isTrackNearEndFromStore(): boolean {
  const { position, duration } = useMaestroStore.getState();
  if (duration <= 0) return true;
  if (position <= 0) return false;
  return position >= duration - 8 || position >= duration * 0.92;
}

/**
 * Gate scheduled auto-advance so wall-clock / fallback cannot pre-empt a
 * position-reporting renderer that is still playing mid-track.
 */
function shouldAllowScheduledAdvance(isFallbackTimer: boolean): boolean {
  const store = useMaestroStore.getState();
  if (store.playbackState !== 'playing' && store.playbackState !== 'stopped') {
    return false;
  }

  if (!isRendererPositionReportingActive()) {
    return true;
  }

  if (isWallClockDueToStaleRelTime()) {
    return true;
  }

  if (isTrackNearEndFromStore()) {
    return true;
  }

  // Fallback only when position updates have gone stale past expected end.
  if (isFallbackTimer && !isRendererPositionReportingActive(12_000)) {
    const { duration } = store;
    if (duration <= 0) return true;
    return isTrackNearEndFromStore();
  }

  return false;
}

async function invokeTrackEndAdvance(): Promise<void> {
  const capturedGen = getAdvanceGeneration();
  const { shouldHonorQueueTrackEndAdvance } = await import('../transport/queueTransportOwnership');
  if (!shouldHonorQueueTrackEndAdvance()) return;
  if (capturedGen !== getAdvanceGeneration()) return;
  const { QueueController } = await import('../transport/QueueController');
  await QueueController.advanceTrackEnd();
}

export function useRemoteAdvanceTimer(): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAtRef = useRef<number>(0);
  const remainingRef = useRef<number>(0);
  const activeTrackIdRef = useRef<string | null>(null);
  const isFallbackTimerRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    isFallbackTimerRef.current = false;
  };

  const fireAdvance = async (isFallback: boolean) => {
    if (!shouldAllowScheduledAdvance(isFallback)) {
      return;
    }

    if (isTestPlaybackMode()) {
      return;
    }

    const sync = getSyncHandler();
    if (sync.isObserving()) {
      const timedForTrackId = activeTrackIdRef.current;
      const waitStart = Date.now();
      while (Date.now() - waitStart < 8000) {
        await new Promise(r => setTimeout(r, 1000));
        const nowId = useMaestroStore.getState().currentTrack?.id || null;
        if (nowId !== timedForTrackId) {
          return;
        }
      }
      return;
    }

    try {
      await invokeTrackEndAdvance();
    } catch (e) {
      console.error('[RemoteAdvanceTimer] Advance failed:', e);
    }
  };

  const startTimer = (remainingSecs: number, zone?: any, opts?: { fallback?: boolean }) => {
    clearTimer();
    const renderer = useMaestroStore.getState().activeConnectorRenderer;
    const useWallClock = shouldUseWallClockAdvance(zone, renderer);
    const fallback = opts?.fallback === true || !useWallClock;

    if (shouldDeferAdvanceToRenderer(zone)) {
      return;
    }

    let delaySecs = remainingSecs;
    if (fallback) {
      delaySecs = Math.max(remainingSecs + WALL_CLOCK_FALLBACK_MARGIN_SECS, WALL_CLOCK_FALLBACK_MARGIN_SECS);
    } else if (!useWallClock) {
      return;
    }

    if (delaySecs <= 0) {
      if (delaySecs > -5 && activeTrackIdRef.current) {
        void fireAdvance(fallback);
      }
      return;
    }

    remainingRef.current = delaySecs;
    startedAtRef.current = Date.now();
    isFallbackTimerRef.current = fallback;

    const timedForTrackId = activeTrackIdRef.current;
    const capturedGen = getAdvanceGeneration();
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      isFallbackTimerRef.current = false;

      if (capturedGen !== getAdvanceGeneration()) {
        return;
      }

      const currentId = useMaestroStore.getState().currentTrack?.id || null;
      if (currentId !== timedForTrackId) {
        return;
      }

      await fireAdvance(fallback);
    }, delaySecs * 1000);
  };

  const pauseTimer = () => {
    if (!timerRef.current) return;
    const elapsed = (Date.now() - startedAtRef.current) / 1000;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    clearTimer();
  };

  const scheduleForTrack = (
    track: any,
    playbackState: string,
    duration: number,
    position: number,
    zone: any,
  ) => {
    const rawDuration = track?.duration || duration || 0;
    const trackDuration = rawDuration > 10000 ? rawDuration / 1000 : rawDuration;
    if (playbackState !== 'playing' || trackDuration <= 0) return;

    const renderer = useMaestroStore.getState().activeConnectorRenderer;
    const useWallClock = shouldUseWallClockAdvance(zone, renderer);
    const remaining = trackDuration - (position || 0);

    if (useWallClock) {
      startTimer(Math.max(remaining, 0) + 2, zone, { fallback: false });
    } else {
      startTimer(Math.max(remaining, 0), zone, { fallback: true });
    }
  };

  useEffect(() => {
    registerRemoteAdvanceCancel(clearTimer);

    const unsubscribe = useMaestroStore.subscribe((state, prevState) => {
      if (prevState) {
        const sameActiveZoneId = state.discovery?.activeZone?.id === prevState.discovery?.activeZone?.id;
        const sameCore =
          sameActiveZoneId
          && state.playbackState === prevState.playbackState
          && state.activeSource === prevState.activeSource
          && state.currentTrack?.id === prevState.currentTrack?.id
          && state.queueIndex === prevState.queueIndex
          && state.queue.length === prevState.queue.length
          && state.duration === prevState.duration
          && state.position === prevState.position;
        const listOnly =
          state.discovery?.discoveredZones !== prevState.discovery?.discoveredZones;
        if (sameCore && listOnly) {
          return;
        }
      }

      const zone = state.discovery?.activeZone;
      const isRemote = isNetworkRenderer(zone);
      const playbackState = state.playbackState;
      const prevPlaybackState = prevState?.playbackState;
      const track = state.currentTrack;
      const trackId = track?.id || null;
      const prevTrackId = prevState?.currentTrack?.id || null;
      const duration = state.duration || 0;
      const position = state.position || 0;

      if (!isRemote) {
        if (timerRef.current) {
          clearTimer();
          activeTrackIdRef.current = null;
        }
        return;
      }

      if (state.activeSource === 'radio') {
        if (timerRef.current) {
          clearTimer();
          activeTrackIdRef.current = null;
        }
        return;
      }

      if (isIOSNativeApp() && isRemote) {
        if (timerRef.current) {
          clearTimer();
          activeTrackIdRef.current = null;
        }
        return;
      }

      if (trackId !== prevTrackId && trackId !== null) {
        activeTrackIdRef.current = trackId;
        clearTimer();
        scheduleForTrack(track, playbackState, duration, position, zone);
        return;
      }

      if (playbackState !== prevPlaybackState) {
        if (playbackState === 'playing' && track && duration > 0) {
          if (shouldDeferAdvanceToRenderer(zone)) {
            clearTimer();
          } else if (remainingRef.current > 0 && prevPlaybackState === 'paused') {
            const renderer = state.activeConnectorRenderer;
            const useWallClock = shouldUseWallClockAdvance(zone, renderer);
            startTimer(remainingRef.current + (useWallClock ? 2 : 0), zone, { fallback: !useWallClock });
          } else {
            scheduleForTrack(track, playbackState, duration, position, zone);
          }
        } else if (playbackState === 'paused') {
          pauseTimer();
        } else if (playbackState === 'stopped' || playbackState === 'idle') {
          const hadActiveTimer = timerRef.current !== null && activeTrackIdRef.current === trackId;
          const wasFallback = isFallbackTimerRef.current;
          clearTimer();
          remainingRef.current = 0;
          if (hadActiveTimer && prevPlaybackState === 'playing' && trackId) {
            const trackIsNearEnd = duration > 0 && position > 0 && (duration - position) < 30;
            if (isTestPlaybackMode()) {
              // Suppressed during test playback
            } else if (getSyncHandler().isObserving()) {
              // Observer waits for controller
            } else if (!trackIsNearEnd) {
              // Stopped early in track — suppressed (transient renderer state)
            } else if (shouldDeferAdvanceToRenderer(zone) || isRendererGaplessAdvanceRecent()) {
              // Renderer owns gapless transition — do not double-advance
            } else if (isRendererPositionReportingActive() && !isTrackNearEndFromStore() && !wasFallback) {
              // Position still mid-track — wait for renderer-track-ended
            } else {
              const capturedGen = getAdvanceGeneration();
              const capturedTrackId = trackId;
              void (async () => {
                if (!isScheduledAdvanceTokenValid(
                  capturedGen,
                  capturedTrackId,
                  useMaestroStore.getState().currentTrack?.id,
                )) {
                  return;
                }
                await invokeTrackEndAdvance();
              })();
            }
          }
        }
        return;
      }

      if (playbackState === 'playing' && track && duration > 0) {
        const prevPosition = prevState?.position || 0;
        const delta = Math.abs(position - prevPosition);
        if (delta > 15) {
          const remaining = duration - position;
          const renderer = state.activeConnectorRenderer;
          const useWallClock = shouldUseWallClockAdvance(zone, renderer);
          if (remaining > 5 && !shouldDeferAdvanceToRenderer(zone)) {
            if (useWallClock) {
              startTimer(remaining + 2, zone, { fallback: false });
            } else {
              startTimer(remaining, zone, { fallback: true });
            }
          }
        }
      }
    });

    return () => {
      unsubscribe();
      clearTimer();
    };
  }, []);
}
